'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';

import ArrowIcon from '@/fo/icons/base/icons/arrow.svg';
import StarIcon from '@/fo/icons/base/icons/rating.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { ReviewsData, Review } from '@/fo/ui/review/catalog/base';
import { ReviewTab, UiReviewTabsBase } from '@/fo/ui/review/tabs/base';

import { ReviewsHeader, ReviewsCarousel } from '../components';

import { styles } from './UiReviewSectionBase.styles';

export interface UiReviewSectionBaseProps
  extends React.HTMLAttributes<HTMLDivElement> {
  reviewsData: ReviewsData;
  onActionButtonClick?: (type: ReviewTab) => void;
  onViewAllClick?: (type: ReviewTab) => void;
  onReviewClick?: (review: Review) => void;
}

export function UiReviewSectionBase({
  reviewsData,
  onActionButtonClick,
  onViewAllClick,
  onReviewClick,
  ...props
}: UiReviewSectionBaseProps) {
  const t = useTranslations();

  const slots = styles();

  const [activeTab, setActiveTab] = useState(ReviewTab.Reviews);

  const tabs = [
    {
      id: ReviewTab.Reviews,
      title: t('review.tabs.reviews'),
      emptyTitle: (
        <div className={slots.emptyTitle()}>
          <StarIcon className={slots.emptyTitleIcon()} />
          {t('review.empty.reviews')}
        </div>
      ),
      count: reviewsData.totalCount
    }
  ];

  const isReviewsTab = activeTab === ReviewTab.Reviews;
  const isEmpty = reviewsData.items.length === 0;

  return (
    <>
      <section
        className={`${slots.base()} ${reviewsData?.items.length > 0 && slots.notEmpty()}`}
        {...props}
      >
        <div className={slots.header()}>
          <div className={slots.tabsContainer()}>
            <UiReviewTabsBase
              tabs={tabs}
              activeTabId={activeTab}
              onTabChange={(tabId) => setActiveTab(tabId)}
            />
            {!isEmpty && (
              <button
                type="button"
                className={slots.mobileButton()}
                onClick={() => onViewAllClick?.(activeTab)}
              />
            )}
          </div>

          <UiKitButtonBase
            color="secondary"
            className={slots.button()}
            onClick={() => onActionButtonClick?.(activeTab)}
          >
            {t('review.buttons.leaveReview')}
          </UiKitButtonBase>

          {isReviewsTab && !isEmpty && (
            <ReviewsHeader
              className={slots.reviewsHeader()}
              reviewsData={reviewsData}
            />
          )}
        </div>

        {!isEmpty && (
          <>
            <div className={slots.contentWrapper()}>
              <ReviewsCarousel
                items={reviewsData.items}
                onReviewClick={onReviewClick}
              />
            </div>

            <button
              className={slots.footerButton()}
              onClick={() => onViewAllClick?.(activeTab)}
              type="button"
            >
              {t('review.buttons.viewAllReviews')}
              <div className={slots.arrowWrapper()}>
                <ArrowIcon className={slots.iconArrow()} />
              </div>
            </button>
          </>
        )}
      </section>
    </>
  );
}
